import { useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import * as yup from 'yup'

import type { ExceptionalDiscountApplyRequest, PolicyVersionRiskItem } from '@/dtos/exceptionalDiscount'
import { formatDate } from '@/utils/formatDate'
import type { DiscountMode } from './lib/premiumMath'
import { computeRiskDiscount, parseCzkInput } from './lib/premiumMath'

export interface SlevaRiskValue {
  mode: DiscountMode
  value: string
}

export interface SlevaFormValues {
  effectiveDate: string
  note: string
  risks: Record<string, SlevaRiskValue>
}

export const tomorrowIso = (): string => {
  const tomorrow = new Date()
  tomorrow.setHours(0, 0, 0, 0)
  tomorrow.setDate(tomorrow.getDate() + 1)

  return formatDate(tomorrow, 'yyyy-MM-dd')
}

const schema = yup.object({
  effectiveDate: yup.string().required('Zadejte počátek slevy.'),
  note: yup.string().trim().required('Poznámka je povinná.'),
  risks: yup.mixed<Record<string, SlevaRiskValue>>().defined(),
})

export const useSlevaForm = (discountableRisks: PolicyVersionRiskItem[]) => {
  const defaultRisks = Object.fromEntries(
    discountableRisks.map(risk => [String(risk.riskType), { mode: 'kc', value: '0' } satisfies SlevaRiskValue]),
  )

  const methods = useForm<SlevaFormValues>({
    defaultValues: {
      effectiveDate: tomorrowIso(),
      note: '',
      risks: defaultRisks,
    },
    resolver: yupResolver(schema) as never,
  })

  const risks = methods.watch('risks')
  const note = methods.watch('note')

  const baseOf = (riskType: number): number =>
    discountableRisks.find(risk => risk.riskType === riskType)?.premiumFrequencyClient ?? 0

  const previewOf = (riskType: number) => {
    const entry = risks?.[String(riskType)]
    if (!entry) return computeRiskDiscount(baseOf(riskType), 'kc', 0)

    return computeRiskDiscount(baseOf(riskType), entry.mode, parseCzkInput(entry.value))
  }

  const setRiskValue = (riskType: number, raw: string) => {
    const entry = risks?.[String(riskType)]
    const mode = entry?.mode ?? 'kc'
    const cleaned = raw.replace(/[^\d\s.,]/g, '').replace('.', ',')
    const numeric = parseCzkInput(cleaned)
    const max = mode === 'percent' ? 100 : baseOf(riskType)
    const clamped = numeric > max ? String(max).replace('.', ',') : cleaned

    methods.setValue(`risks.${riskType}.value` as never, clamped as never, { shouldValidate: false })
  }

  const setRiskMode = (riskType: number, mode: DiscountMode) => {
    const entry = risks?.[String(riskType)]
    const numeric = Math.min(parseCzkInput(entry?.value), mode === 'percent' ? 100 : baseOf(riskType))

    methods.setValue(`risks.${riskType}.mode` as never, mode as never, { shouldValidate: false })
    methods.setValue(`risks.${riskType}.value` as never, String(numeric).replace('.', ',') as never, { shouldValidate: false })
  }

  const hasAnyValue = discountableRisks.some(risk => parseCzkInput(risks?.[String(risk.riskType)]?.value) > 0)
  const canSubmit = hasAnyValue && !!note?.trim()

  const toRequest = (insuranceId: number, values: SlevaFormValues): ExceptionalDiscountApplyRequest => ({
    insuranceId,
    note: values.note.trim(),
    effectiveDate: values.effectiveDate,
    risks: discountableRisks
      .map(risk => ({ riskType: risk.riskType, entry: values.risks[String(risk.riskType)] }))
      .filter(({ entry }) => parseCzkInput(entry?.value) > 0)
      .map(({ riskType, entry }) => ({
        riskType,
        amountKc: entry.mode === 'kc' ? parseCzkInput(entry.value) : undefined,
        percent: entry.mode === 'percent' ? parseCzkInput(entry.value) : undefined,
      })),
  })

  return { methods, previewOf, setRiskValue, setRiskMode, baseOf, hasAnyValue, canSubmit, toRequest }
}
